import React, { useEffect, useMemo, useState } from 'react';
import { Save, X } from 'lucide-react';
import closeIntelApi from './closeIntelApi';
import SectionGuide from './SectionGuide';
import { formatExpectationUnit, splitCompanyExpectations } from './expectationsHelpers';
import {
  DEFAULT_BASIS,
  DEFAULT_LIMIT,
  FORECAST_BASES,
  buildForecastContextParams,
  buildForecastOverridePayload,
  canSaveOverride,
  collectForecastScopeOptions,
  formatForecastAmount,
  formatVariancePct,
  forecastOverrideStatus,
  normalizeForecastRows,
  previousMonthKey,
} from './forecastContextHelpers';

const ALL = 'All';

const sourceChipLabel = (source) => {
  const labels = { fdic_seed: 'FDIC-seeded', manual: 'Manual', peer_lens: 'Peer Lens' };
  return labels[source] || source || 'Source n/a';
};

const errorText = (error) => {
  const detail = error?.response?.data?.error ?? error?.message ?? error;
  return typeof detail === 'string' ? detail : JSON.stringify(detail);
};

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// ---------------------------------------------------------------- company guardrails

function GuardrailEditDrawer({ row, onSave, onClose, saving }) {
  const [draft, setDraft] = useState({
    tolerance_low: row.tolerance_low ?? '',
    target_value: row.target_value ?? '',
    tolerance_high: row.tolerance_high ?? '',
  });
  const field = (key, label) => (
    <label className="ci-fc-field">
      <span className="ace-eyebrow">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        className="ace-input"
        value={draft[key]}
        onChange={(event) => setDraft((d) => ({ ...d, [key]: event.target.value }))}
        aria-label={`${row.metric_label || row.metric_key} ${label}`}
      />
    </label>
  );
  return (
    <div className="ci-fc-drawer" role="group" aria-label={`Edit ${row.metric_label || row.metric_key}`}>
      <div className="ci-fc-drawer-head">
        <strong>{row.metric_label || row.metric_key}</strong>
        <button type="button" className="ace-pill ci-fc-icon-btn" onClick={onClose} aria-label="Close editor">
          <X size={13} />
        </button>
      </div>
      <div className="ci-fc-drawer-fields">
        {field('tolerance_low', 'Tolerance low')}
        {field('target_value', 'Target')}
        {field('tolerance_high', 'Tolerance high')}
      </div>
      <div className="ci-fc-drawer-actions">
        <button
          type="button"
          className="ace-pill ci-pinpoint-button ci-tip"
          data-tip="Write this band to expectations_company — takes effect next monitor run"
          disabled={saving}
          onClick={() =>
            onSave({
              ...row,
              tolerance_low: num(draft.tolerance_low),
              target_value: num(draft.target_value),
              tolerance_high: num(draft.tolerance_high),
            })
          }
        >
          <Save size={13} /> {saving ? 'Saving…' : 'Save guardrail'}
        </button>
      </div>
    </div>
  );
}

function CompanyGuardrailsPanel() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setError('');
    try {
      const payload = await closeIntelApi.listExpectations('company');
      setRows(payload?.rows || []);
    } catch (e) {
      setError(errorText(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const { internal } = splitCompanyExpectations(rows);

  const onSave = async (row) => {
    const rowId = row.id || row.metric_key || row.metricKey;
    setSaving(true);
    setError('');
    try {
      const saved = await closeIntelApi.saveExpectation('company', row);
      setRows((current) =>
        current.map((item) => ((item.id || item.metric_key || item.metricKey) === rowId ? { ...item, ...saved } : item)),
      );
      setEditing('');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="ace-panel ci-fc-panel">
      <div className="ci-fc-panel-head">
        <div>
          <div className="ace-eyebrow">Company guardrails</div>
          <p className="ci-fc-panel-sub">
            Editable, board-cited bands anchored on BUDGET_FY2026 / current plan. Breaches surface in the CI_01 monitor
            and CI_05 board rail.
          </p>
        </div>
      </div>
      {error && <p className="ci-error" style={{ marginTop: 8 }}>{error}</p>}
      <div className="ci-table-wrap">
        <table className="ci-fc-table ci-fc-guardrail-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th className="ci-fc-num">Low</th>
              <th className="ci-fc-num">Target</th>
              <th className="ci-fc-num">High</th>
              <th>Source</th>
              <th aria-label="Edit" />
            </tr>
          </thead>
          <tbody>
            {internal.map((row) => {
              const rowId = row.id || row.metric_key || row.metricKey;
              const unit = row.unit || row.value_unit;
              const fmt = (value) => formatExpectationUnit(value, unit);
              const isEditing = editing === rowId;
              return (
                <React.Fragment key={rowId}>
                  <tr className={isEditing ? 'ci-fc-row-active' : ''}>
                    <td>
                      <strong>{row.metric_label || row.metric_key || rowId}</strong>
                      {row.narrative_note && <span className="ci-fc-note">{row.narrative_note}</span>}
                    </td>
                    <td className="ci-fc-num">{fmt(row.tolerance_low)}</td>
                    <td className="ci-fc-num">{fmt(row.target_value)}</td>
                    <td className="ci-fc-num">{fmt(row.tolerance_high)}</td>
                    <td>
                      <span className="ci-fc-source-chip">{sourceChipLabel(row.source)}</span>
                    </td>
                    <td className="ci-fc-num">
                      <button
                        type="button"
                        className="ace-pill ci-mini-link"
                        onClick={() => setEditing(isEditing ? '' : rowId)}
                      >
                        {isEditing ? 'Close' : 'Edit'}
                      </button>
                    </td>
                  </tr>
                  {isEditing && (
                    <tr className="ci-fc-drawer-row">
                      <td colSpan={6}>
                        <GuardrailEditDrawer
                          row={row}
                          onSave={onSave}
                          onClose={() => setEditing('')}
                          saving={saving}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {!internal.length && !error && (
              <tr>
                <td colSpan={6} className="ci-fc-empty">No company guardrails returned.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- unit inspector

function OverrideEditor({ row, basis, onSaved }) {
  const prefill = row.expectedValue ?? row.sourceValue ?? '';
  const [draft, setDraft] = useState({ overrideValue: prefill, note: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Re-seed the draft whenever the inspected row changes.
  useEffect(() => {
    setDraft({ overrideValue: row.expectedValue ?? row.sourceValue ?? '', note: '' });
    setError('');
    setSaving(false);
  }, [row.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const valid = canSaveOverride({
    overrideValue: draft.overrideValue,
    note: draft.note,
    sourceVersion: row.sourceVersion,
  });

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = buildForecastOverridePayload(row, {
        overrideValue: draft.overrideValue,
        note: draft.note,
        basis,
      });
      await closeIntelApi.saveForecastOverride(payload);
      // Refresh the baseline page so backend precedence reflects the new active override.
      onSaved();
    } catch (e) {
      setError(errorText(e));
      setSaving(false);
    }
  };

  return (
    <div className="ci-fc-override-detail" role="group" aria-label="Save forecast override">
      <div className="ace-eyebrow">Override this baseline</div>
      <p className="ci-fc-note">
        Departs from {formatForecastAmount(row.sourceValue)} ({row.sourceVersion || 'source'}). Requires a note; the
        active override then supersedes the baseline value.
      </p>
      <div className="ci-fc-drawer-fields" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
        <label className="ci-fc-field">
          <span className="ace-eyebrow">Override value</span>
          <input
            type="number"
            inputMode="decimal"
            className="ace-input"
            value={draft.overrideValue}
            onChange={(event) => setDraft((d) => ({ ...d, overrideValue: event.target.value }))}
            aria-label="Override value"
          />
        </label>
        <label className="ci-fc-field">
          <span className="ace-eyebrow">Note (required)</span>
          <textarea
            className="ace-input"
            rows={2}
            placeholder="Why this departs from the forecast baseline"
            value={draft.note}
            onChange={(event) => setDraft((d) => ({ ...d, note: event.target.value }))}
            aria-label="Override note"
          />
        </label>
      </div>
      {error && <p className="ci-error" style={{ marginTop: 6 }}>{error}</p>}
      <div className="ci-fc-drawer-actions" style={{ marginTop: 8 }}>
        <button
          type="button"
          className="ace-pill ci-pinpoint-button ci-tip"
          data-tip="Write an audited override to forecast_context_override — supersedes any prior active override"
          disabled={saving || !valid}
          onClick={submit}
        >
          <Save size={13} /> {saving ? 'Saving…' : 'Save override'}
        </button>
      </div>
    </div>
  );
}

function RowInspector({ row, basis, onClose, onSaved }) {
  if (!row) return null;
  const override = row.override || null;
  const items = [
    ['Source version', row.sourceVersion || '—'],
    ['Source label', row.source || '—'],
    ['Source value', formatForecastAmount(row.sourceValue)],
    ['Actual value', formatForecastAmount(row.actualValue)],
    ['Signed variance', formatForecastAmount(row.varianceValue)],
    ['Variance %', formatVariancePct(row.variancePct)],
    ['Source row count', row.sourceRowCount ?? '—'],
    ['Override status', forecastOverrideStatus(row)],
  ];
  return (
    <aside className="ci-fc-inspector ace-panel" aria-label="Forecast baseline row inspector">
      <div className="ci-fc-inspector-head">
        <div>
          <div className="ace-eyebrow">Row lineage</div>
          <strong>{row.planningEntity} · {row.metricLabel || row.metricKey}</strong>
        </div>
        <button type="button" className="ace-pill ci-fc-icon-btn" onClick={onClose} aria-label="Close inspector">
          <X size={13} />
        </button>
      </div>
      <dl className="ci-fc-inspector-grid">
        {items.map(([label, value]) => (
          <div key={label} className="ci-fc-inspector-item">
            <dt className="ace-eyebrow">{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      {override && (
        <div className="ci-fc-override-detail">
          <div className="ace-eyebrow">Active override</div>
          <p className="ci-fc-note">
            {formatForecastAmount(override.override_value)} · {override.note || 'No note'}
            {override.created_by ? ` · ${override.created_by}` : ''}
            {override.createdAt ? ` · ${override.createdAt}` : ''}
          </p>
        </div>
      )}
      <OverrideEditor row={row} basis={basis} onSaved={onSaved} />
    </aside>
  );
}

// ---------------------------------------------------------------- page

const cleanScope = (value) => (value && value !== ALL ? value : '');

export default function ForecastContextPage() {
  const [filters, setFilters] = useState({
    period: previousMonthKey(),
    basis: DEFAULT_BASIS,
    market: ALL,
    lob: ALL,
    entity: '',
    metricKey: ALL,
  });
  const [data, setData] = useState(() => normalizeForecastRows(null));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState('');

  const limit = DEFAULT_LIMIT;

  const load = async (nextOffset) => {
    setLoading(true);
    setError('');
    const params = buildForecastContextParams({
      period: filters.period,
      basis: filters.basis,
      market: cleanScope(filters.market),
      lob: cleanScope(filters.lob),
      entity: filters.entity,
      metricKey: cleanScope(filters.metricKey),
      limit,
      offset: nextOffset,
    });
    try {
      const payload = await closeIntelApi.listForecastContextUnits(params);
      setData(normalizeForecastRows(payload));
      setSelectedId('');
    } catch (e) {
      setError(errorText(e));
      setData(normalizeForecastRows(null));
    } finally {
      setLoading(false);
    }
  };

  // Reload on filter change (resets to first page); offset changes reload the page.
  useEffect(() => {
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.period, filters.basis, filters.market, filters.lob, filters.entity, filters.metricKey]);

  const { rows, pagination, forecastVersion, versionProvenance, basis } = data;
  const options = useMemo(() => collectForecastScopeOptions(rows), [rows]);
  const selectedRow = rows.find((row) => row.id === selectedId) || null;

  const setFilter = (patch) => setFilters((current) => ({ ...current, ...patch }));

  const goToPage = (nextOffset) => {
    load(Math.max(0, nextOffset));
  };

  const shownFrom = rows.length ? pagination.offset + 1 : 0;
  const shownTo = pagination.offset + rows.length;

  return (
    <section className="ace-panel ci-fc-shell" style={{ padding: 18, borderRadius: 16 }}>
      <div style={{ marginBottom: 12 }}>
        <div className="ace-eyebrow">CI_03 <span aria-hidden>●</span> Forecast context</div>
        <h2 style={{ margin: '5px 0 0' }}>Forecast baselines, company guardrails, and cited context</h2>
        <SectionGuide guideId="expectations" />
      </div>

      <CompanyGuardrailsPanel />

      <section className="ace-panel ci-fc-panel" style={{ marginTop: 18 }}>
        <div className="ci-fc-panel-head">
          <div>
            <div className="ace-eyebrow">Unit forecast baseline</div>
            <p className="ci-fc-panel-sub">
              Read-only. The expectation for each unit equals the latest governed forecast value for the selected
              period — no second source of truth to hand-maintain. Overrides are the only audited departure.
            </p>
          </div>
        </div>

        <div className="ci-fc-strip ci-mono">
          <span>PERIOD {data.period || filters.period}</span>
          <span>BASIS {basis}</span>
          <span>VERSION {forecastVersion || '—'}</span>
          {versionProvenance?.resolvedFrom && <span>RESOLVED {versionProvenance.resolvedFrom}</span>}
          <span>ROWS {pagination.total}</span>
        </div>

        <div className="ci-fc-filters">
          <label className="ci-fc-field">
            <span className="ace-eyebrow">Period</span>
            <input
              type="month"
              className="ace-input"
              value={filters.period}
              onChange={(event) => setFilter({ period: event.target.value })}
            />
          </label>
          <label className="ci-fc-field">
            <span className="ace-eyebrow">Basis</span>
            <select
              className="ace-input"
              value={filters.basis}
              onChange={(event) => setFilter({ basis: event.target.value })}
            >
              {FORECAST_BASES.map((b) => (
                <option key={b} value={b}>{b === 'prior' ? 'Latest forecast (prior)' : 'Budget / plan'}</option>
              ))}
            </select>
          </label>
          <label className="ci-fc-field">
            <span className="ace-eyebrow">Market</span>
            <select
              className="ace-input"
              value={filters.market}
              onChange={(event) => setFilter({ market: event.target.value })}
            >
              <option value={ALL}>All markets</option>
              {options.markets.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>
          <label className="ci-fc-field">
            <span className="ace-eyebrow">LOB</span>
            <select
              className="ace-input"
              value={filters.lob}
              onChange={(event) => setFilter({ lob: event.target.value })}
            >
              <option value={ALL}>All LOBs</option>
              {options.lobs.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </label>
          <label className="ci-fc-field">
            <span className="ace-eyebrow">Metric</span>
            <select
              className="ace-input"
              value={filters.metricKey}
              onChange={(event) => setFilter({ metricKey: event.target.value })}
            >
              <option value={ALL}>All metrics</option>
              {options.metrics.map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </label>
          <label className="ci-fc-field ci-fc-field-grow">
            <span className="ace-eyebrow">Entity filter</span>
            <input
              type="text"
              className="ace-input"
              placeholder="PLN_COMM_ATLANTA"
              value={filters.entity}
              onChange={(event) => setFilter({ entity: event.target.value })}
            />
          </label>
        </div>

        {error && <p className="ci-error" style={{ marginTop: 10 }}>{error}</p>}

        <div className="ci-fc-body">
          <div className="ci-table-wrap ci-fc-table-wrap">
            <table className="ci-fc-table ci-fc-unit-table">
              <thead>
                <tr>
                  <th>Entity</th>
                  <th>Market</th>
                  <th>LOB</th>
                  <th>Metric</th>
                  <th>Period</th>
                  <th className="ci-fc-num">Latest forecast</th>
                  <th className="ci-fc-num">Actual</th>
                  <th className="ci-fc-num">Variance</th>
                  <th>Source version</th>
                  <th>Override</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const varianceNegative = num(row.varianceValue) < 0;
                  const status = forecastOverrideStatus(row);
                  return (
                    <tr
                      key={row.id}
                      className={`ci-fc-unit-row${selectedId === row.id ? ' ci-fc-row-active' : ''}`}
                      onClick={() => setSelectedId(selectedId === row.id ? '' : row.id)}
                    >
                      <td><strong>{row.planningEntity}</strong></td>
                      <td>{row.market || '—'}</td>
                      <td>{row.lob || '—'}</td>
                      <td>{row.metricLabel || row.metricKey}</td>
                      <td className="ci-mono">{row.period}</td>
                      <td className="ci-fc-num">{formatForecastAmount(row.expectedValue)}</td>
                      <td className="ci-fc-num">{formatForecastAmount(row.actualValue)}</td>
                      <td className={`ci-fc-num${varianceNegative ? ' ci-amount negative' : ''}`}>
                        {formatForecastAmount(row.varianceValue)}
                        <span className="ci-fc-var-pct">{formatVariancePct(row.variancePct)}</span>
                      </td>
                      <td className="ci-mono">{row.sourceVersion || '—'}</td>
                      <td>
                        <span className={`ci-fc-status-chip${status === 'Override' ? ' ci-fc-status-override' : ''}`}>
                          {status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {!rows.length && (
                  <tr>
                    <td colSpan={10} className="ci-fc-empty">
                      {loading ? 'Loading forecast baselines…' : 'No unit forecast baselines for this scope.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {selectedRow && (
            <RowInspector
              row={selectedRow}
              basis={basis}
              onClose={() => setSelectedId('')}
              onSaved={() => load(pagination.offset)}
            />
          )}
        </div>

        <div className="ci-fc-pager">
          <span className="ci-mono">
            {shownFrom}–{shownTo} of {pagination.total}
          </span>
          <div className="ci-fc-pager-actions">
            <button
              type="button"
              className="ace-pill ci-mini-link"
              disabled={loading || pagination.offset <= 0}
              onClick={() => goToPage(pagination.offset - limit)}
            >
              Prev
            </button>
            <button
              type="button"
              className="ace-pill ci-mini-link"
              disabled={loading || shownTo >= pagination.total}
              onClick={() => goToPage(pagination.offset + limit)}
            >
              Next
            </button>
          </div>
        </div>
      </section>
    </section>
  );
}
